/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import RenderDocument from "js_renderer";
import {mat4, vec3, vec2} from "gl-matrix";
import IBoundingBox from "../../bounding_box_interface";
import IModel from "../../structural_elements/model_interface";
import Plane from "../../elements/plane";
import Curve from "../../elements/curve";
import IClippingPlaneModule from "./scene_clipping_plane_interface";
import Scene from "../../scene";
import ViewModule from "../view/scene_view";
import RenderSettingsModule from "../render_settings/scene_render_settings";
import PickingModule from "../picking/scene_picking";
import Settings from "../settings";

interface IMousePosition {
    x: number;
    y: number;
}

interface IClippingPlaneSettings {
    enabled?: boolean;
    selected?: boolean;
    highlightCut?: boolean;
    planeVisible?: boolean;
    planeDefaultRotationVec?: vec3;
    planeDefaultTiltVec?: vec3;
    accumulatedDelta?: number;
}

export default
class ClippingPlaneModule extends AbstractModule implements IClippingPlaneModule {

    protected static _typeName: string = "clippingPlane";

    protected _defaultSettings: IClippingPlaneSettings;
    protected _settings: IClippingPlaneSettings;

    private _planeSAPIElement: Plane;
    private _onhoverPlaneSAPIElement: Plane;
    private _planeOutlineSAPIElement: Curve;
    private _elementPickingAlreadyEnabled: boolean;

    private _hovered: boolean = false;
    private _draggable: boolean = false;
    private _lastMousePosition: IMousePosition = { x: 0, y: 0};

    private _yRotationOffset: number = 0;
    private _rotationMat: mat4 = mat4.create();
    private _transformationMat: mat4 = mat4.create();
    private _translation: vec3 = vec3.create();
    private _normal: vec3 = vec3.create();

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);
        this._type = ClippingPlaneModule._typeName;

        this._planeSAPIElement = null;
        this._onhoverPlaneSAPIElement = null;
        this._planeOutlineSAPIElement = null;

        this._defaultSettings = {
            enabled: false,
            selected: false,
            highlightCut: true,
            planeVisible: true,
            planeDefaultRotationVec: vec3.create(),
            planeDefaultTiltVec: vec3.create(),
            accumulatedDelta: 0,
        };
        this._settings = Settings.clone(this._defaultSettings);

        this._elementPickingAlreadyEnabled = false;
    }

    public static getClassType(): string {
        return ClippingPlaneModule._typeName;
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public isEnabled(): boolean {
        return this._settings.enabled;
    }

    public addYRotationOffset(aIncrement: number) {

        this._yRotationOffset += aIncrement;
        this.updateYrotationOffset();

        // the clipping plane rotation should be stored as a general value instead of per model as we could have more models in the scene
        // so we need the general value

        /*let mainModel: IModel = this.sceneAPI.getDocument().getMainModel();
        let modelConfiguration = mainModel.getModelConfiguration();
        modelConfiguration.clippingPlaneModule.yRotationOffset = this._settings.yRotationOffset;
        mainModel.setModelConfiguration(modelConfiguration);

        // DO we need it with the save general functionality?
        // save it to DOM instantly
        let script = document.getElementById('modelDescriptionFor_scene1');
        if (script) {
            let indexStart = script.innerHTML.indexOf("\"yRotationOffset\":");
            let indexEnd = script.innerHTML.indexOf("}", indexStart);

            let substr = script.innerHTML.substr(indexStart, indexEnd - indexStart);
            script.innerHTML = script.innerHTML.replace(substr, "\"yRotationOffset\":" + this._settings.yRotationOffset);
        }

        this.updateYrotationOffset();*/
    }

    public moveInNormalDirection(aDelta: number) {
        this.set({"accumulatedDelta" : this._settings.accumulatedDelta + aDelta});
    }

    public setPlane(aRotationVector: vec3, aTiltRotationVector: vec3) {
        this._settings.planeDefaultRotationVec = aRotationVector;
        this._settings.planeDefaultTiltVec = aTiltRotationVector;

        // compute rotation from the normal
        mat4.identity(this._rotationMat);
        mat4.rotateX(this._rotationMat, this._rotationMat, aRotationVector[0]);
        if (aRotationVector[0] === 0) { // except the xy plane
            mat4.rotateY(this._rotationMat, this._rotationMat, aRotationVector[1] + this._yRotationOffset);
            mat4.rotateZ(this._rotationMat, this._rotationMat, aRotationVector[2]);
        } else {
            mat4.rotateY(this._rotationMat, this._rotationMat, aRotationVector[1]);
            if (aRotationVector[0] > 0) {
                mat4.rotateZ(this._rotationMat, this._rotationMat, aRotationVector[2] - this._yRotationOffset);
            } else {
                mat4.rotateZ(this._rotationMat, this._rotationMat, aRotationVector[2] + this._yRotationOffset);
            }
        }

        mat4.rotateX(this._rotationMat, this._rotationMat, aTiltRotationVector[0]);
        mat4.rotateY(this._rotationMat, this._rotationMat, aTiltRotationVector[1]);
        mat4.rotateZ(this._rotationMat, this._rotationMat, aTiltRotationVector[2]);

        this._recomputePlane();
    }

    public recenter() {
        this.set({"accumulatedDelta": 0});
    }

    public isCutsVisible(): boolean {
        return this._settings.highlightCut;
    }

    public getPlaneEquation(): number[] {
        let d = -this._normal[0] * this._translation[0] - this._normal[1] * this._translation[1] - this._normal[2] * this._translation[2];
        return [this._normal[0], this._normal[1], this._normal[2], d];
    }

    public onMousePress(x: number, y: number, buttonState: number) {
        if (!this._settings.selected || !this._planeSAPIElement) {
            return;
        }

        let sapielement = this.sceneAPI.pickingModule.pickSAPIElement();
        if ((sapielement === this._planeSAPIElement || sapielement === this._onhoverPlaneSAPIElement)) {
            this._draggable = true;

            // this.sceneAPI.viewModule._getActiveView().getViewControls().enable(false);
            this.sceneAPI.pickingModule.disableCameraRotation(true);
        }
    }

    public onTouchStart(x: number, y: number, numTouches: number) {
        this.onMousePress(x, y, 0);
    }

    public onMouseRelease(x, y, buttonState) {
        if (this._draggable) {
            this._draggable = false;
            // this.sceneAPI.viewModule._getActiveView().getViewControls().enable(true);
            this.sceneAPI.pickingModule.disableCameraRotation(false);

            this._lastMousePosition.x = 0;
            this._lastMousePosition.y = 0;
        }
    }

    public onTouchEnd(x: number, y: number, numTouches: number) {
        this.onMouseRelease(x, y, 0);
    }

    public onClick(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean) {
        if (aDraggedDuringClick || !this._planeSAPIElement) {
            return;
        }

        let sapielement = this.sceneAPI.pickingModule.pickSAPIElement();
        if ((sapielement === this._planeSAPIElement || sapielement === this._onhoverPlaneSAPIElement) && !this._settings.selected) {
            if (!this._settings.selected) {
                this._settings.selected = true;
                this.updatePlanesVisualization();
            }
        } else {
            // unselect
            if (this._settings.selected) {
                this._settings.selected = false;
                this.updatePlanesVisualization();

                this._lastMousePosition.x = 0;
                this._lastMousePosition.y = 0;
            }
        }
    }

    public onSingleTap(x: number, y: number) {
        this.onClick(x, y, 0, false);
    }

    public onMove(x: number, y: number, delta: number, buttonState: number) {
        if (!this._planeSAPIElement || !this._settings.planeVisible) {
            return;
        }

        let sapielement = this.sceneAPI.pickingModule.pickSAPIElement();
        if (sapielement === this._planeSAPIElement || sapielement === this._onhoverPlaneSAPIElement) {
            if (!this._hovered) {
                this._hovered = true;
                this.updatePlanesVisualization();
            }
        } else {
            if (this._hovered) {
                this._hovered = false;
                this.updatePlanesVisualization();
            }
        }
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number) {
        if (!this.sceneAPI.viewModeModule.getActiveView().getControls().isRotating(buttonState)) {
            return;
        }

        if (this._draggable) {
            if (!this._lastMousePosition.x) {
                this._lastMousePosition.x = x;
                this._lastMousePosition.y = y;
                return;
            }

            // we should project the plane normal to screen and compute angle between it and the drag vector
            let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();
            let nStartPoint3d = _sceneAPIBBox.getCenter();
            let nEndPoint3d = vec3.create();
            vec3.add(nEndPoint3d, nStartPoint3d, this._normal);

            let view = this.sceneAPI.viewModeModule.getActiveView();
            let nStartPoint2d = vec3.clone(view.projectPointToWindow(nStartPoint3d));
            let nEndPoint2d = vec3.clone(view.projectPointToWindow(nEndPoint3d));

            let normalVec2d = vec2.fromValues(nEndPoint2d[0] - nStartPoint2d[0], nEndPoint2d[1] - nStartPoint2d[1]);
            let dragVec2d = vec2.fromValues((x - this._lastMousePosition.x), (y - this._lastMousePosition.y));

            let step = vec2.length(dragVec2d) / vec2.length(normalVec2d);

            vec2.normalize(normalVec2d, normalVec2d);
            vec2.normalize(dragVec2d, dragVec2d);

            let angle = Math.acos(vec2.dot(normalVec2d, dragVec2d));
            if (angle < (Math.PI / 2.0)) {
                this.moveInNormalDirection(step);
            } else {
                this.moveInNormalDirection(-step);
            }

            this._lastMousePosition.x = x;
            this._lastMousePosition.y = y;
        }
    }

    public onTouchMove(x: number, y: number, delta: number, buttonState: number) {
        this.onDrag(x, y, 0, 1);
    }

    public setFromModelDescription(aJsonObject: any) {
        let settings: IClippingPlaneSettings = {};
        if (aJsonObject.hasOwnProperty(this.getType())) {
            settings = aJsonObject[this.getType()];
        }
        this.set(settings);
    }

    protected updateYrotationOffset() {
        this.setPlane(this._settings.planeDefaultRotationVec, this._settings.planeDefaultTiltVec);
    }

    protected setEnabled() {

        // was off, now on
        if (this._settings.enabled) {

            this._resetSettings();

            this._planeSAPIElement = <Plane>this.sceneDocument.createAPIElement(Plane.TypeName);
            this.sceneDocument.attachElementToInternalModel(this._planeSAPIElement);

            let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();
            let positions = Plane.getPositionsFromRadius(_sceneAPIBBox.getRadius() / 2.0, this._transformationMat);

            // var lightBackground =  cadwork.webgl.SceneAPI.BackgroundModule.isColorLight(this._sceneAPI.backgroundModule.getBackgroundColorRef255());
            let color = vec3.fromValues(0.5, 0.5, 0.5); // lightBackground ? vec3.fromValues(0.5, 0.5, 0.5) : vec3.fromValues(0.5, 0.5, 0.5);

            this._planeSAPIElement.setPositions(new Float32Array(positions));
            this._planeSAPIElement.extendProperties({clippable: false, shading: 0, transparency: 0.4, color: color});
            this._planeSAPIElement.draw(true); // TB? true / false
            this._planeSAPIElement.setVisibility(this._settings.planeVisible);

            // create on hover sapi element
            this._onhoverPlaneSAPIElement = <Plane>this.sceneDocument.createAPIElement(Plane.TypeName);
            this.sceneDocument.attachElementToInternalModel(this._onhoverPlaneSAPIElement);
            this._onhoverPlaneSAPIElement.setPositions(new Float32Array(positions));
            this._onhoverPlaneSAPIElement.extendProperties({
                clippable: false,
                shading: 0,
                transparency: 0.2,
                color: color
            });
            this._onhoverPlaneSAPIElement.draw(true);
            this._onhoverPlaneSAPIElement.setVisibility(false);

            // create outline
            this._planeOutlineSAPIElement = <Curve>this.sceneDocument.createAPIElement(Curve.TypeName);
            this.sceneDocument.attachElementToInternalModel(this._planeOutlineSAPIElement);
            this._planeOutlineSAPIElement.setPositionsFromLineStripLoop(positions);
            this._planeOutlineSAPIElement.extendProperties({notOutline: true, clippable: false, contributeToNearPlaneClipping: false});
            this._planeOutlineSAPIElement.draw(true);
            this._planeOutlineSAPIElement.setVisibility(this._settings.planeVisible);

            this.sceneAPI.getRenderSettingsModule().set({geometry: {forceBackfaceCullingOff: true}});
            this.sceneAPI.viewModeModule.getActiveView().registerUserInputObserver(this);

            this._elementPickingAlreadyEnabled = !this.sceneAPI.pickingModule.startElementPicking({});
        }
        else  if (this._planeSAPIElement) {
            this._planeSAPIElement.remove();
            this._onhoverPlaneSAPIElement.remove();
            this._planeOutlineSAPIElement.remove();

            this.sceneAPI.getRenderSettingsModule().set({geometry: {forceBackfaceCullingOff: false}});
            this.sceneAPI.viewModeModule.getActiveView().unregisterUserInputObserver(this);

            if (!this._elementPickingAlreadyEnabled) {
                this.sceneAPI.pickingModule.stopElementPicking();
            }

            this._planeSAPIElement = null;
        }

        // recompile shaders
        let webglRenderGraph = this.renderDocument.getRenderer().getWebGLRenderGraph();
        if (webglRenderGraph) {
            webglRenderGraph.initializeShaders();
        }
    }

    protected setPlaneVisible(aValue: boolean) {
        if (this._planeSAPIElement && !aValue) {
            this._planeSAPIElement.setVisibility(false);
            this._onhoverPlaneSAPIElement.setVisibility(false);
            this._planeOutlineSAPIElement.setVisibility(false);
        } else if (this._planeSAPIElement) {
            this.updatePlanesVisualization();
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {

        if (aChangedSettings.hasOwnProperty("planeVisible")) {
            this.setPlaneVisible(aChangedSettings.planeVisible);
        }

        if (aChangedSettings.hasOwnProperty("highlightCut")) {
            this.sceneAPI.viewModeModule.scheduleRedraw();
        }

        if (aChangedSettings.hasOwnProperty("enabled")) {
            this.setEnabled();
        }

        if (aChangedSettings.hasOwnProperty("accumulatedDelta")) {
            this._recomputePlane();
        }
    }

    private updatePlanesVisualization() {
        if (this._settings.selected) {
            this._planeSAPIElement.setVisibility(false);
            this._onhoverPlaneSAPIElement.setVisibility(this._settings.planeVisible);
            this._onhoverPlaneSAPIElement.select(true);

            this._planeOutlineSAPIElement.setVisibility(this._settings.planeVisible);
        } else if (this._hovered) {
            this._planeSAPIElement.setVisibility(false);
            this._onhoverPlaneSAPIElement.setVisibility(this._settings.planeVisible);
            this._onhoverPlaneSAPIElement.unselect();

            this._planeOutlineSAPIElement.setVisibility(this._settings.planeVisible);
        } else {
            this._planeSAPIElement.setVisibility(true);
            this._onhoverPlaneSAPIElement.setVisibility(false);
            this._onhoverPlaneSAPIElement.unselect();

            this._planeOutlineSAPIElement.setVisibility(this._settings.planeVisible);
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    private _recomputePlane() {
        vec3.transformMat4(this._normal, [0, 0, -1], this._rotationMat);

        let _sceneAPIBBox: IBoundingBox = this.sceneDocument.getSBoundingBoxForNPC();
        vec3.scaleAndAdd(this._translation, _sceneAPIBBox.getCenter(), this._normal, this._settings.accumulatedDelta);

        // first rotate the matrix then move it to the desired center
        mat4.identity(this._transformationMat);
        mat4.translate(this._transformationMat, this._transformationMat, this._translation);
        mat4.multiply(this._transformationMat, this._transformationMat, this._rotationMat);

        // do not ths the sapi element sooner than here as we need to remember the transformation
        if (!this._planeSAPIElement) {
            return;
        }

        let positions = Plane.getPositionsFromRadius(_sceneAPIBBox.getRadius() / 2.0, this._transformationMat);
        this._planeSAPIElement.setPositions(new Float32Array(positions));
        this._onhoverPlaneSAPIElement.setPositions(new Float32Array(positions));
        this._planeOutlineSAPIElement.setPositionsFromLineStripLoop(positions);

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    private _resetSettings() {
        this._settings.selected = false;
        this._hovered = false;
        this._draggable = false;
    }

}

Scene.registerSceneAPIModule(ClippingPlaneModule);
